import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Rocket, Sparkles } from 'lucide-react';
import Button from './ui/Button';

interface UpdateModalProps {
  update: {
    version: string;
    title: string;
    description: string;
    features: string[];
    date: string;
  };
  onClose: () => void;
}

const UpdateModal: React.FC<UpdateModalProps> = ({ update, onClose }) => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-background flex items-center justify-center"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="w-full max-w-2xl mx-auto p-8 text-center"
        >
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 text-sm font-medium rounded-full bg-primary text-primary-foreground">
              <Sparkles className="w-4 h-4" />
              Version {update.version}
            </div>
            <h2 className="text-4xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-primary via-purple-500 to-primary">
              {update.title}
            </h2>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto">
              {update.description}
            </p>
          </motion.div>

          <div
              className="space-y-4 mb-12 pl-6 md:pl-14"
          >
            {update.features.map((feature, index) => {
              const [emoji, ...text] = feature.split(' ');
              return (
                  <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 + index * 0.1 }}
                      className="flex items-center gap-3 justify-start"
                  >
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-lg">
                      {emoji}
                    </div>
                    <span className="text-sm md:text-lg break-words">
                      {text.join(' ')}
                    </span>
                  </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6 }}
          >
            <Button
              size="lg"
              onClick={onClose}
              className="group relative overflow-hidden"
            >
              <span className="relative z-10 flex items-center gap-2">
                <Rocket className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                Let's Go!
              </span>
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-primary via-purple-500 to-primary"
                initial={{ x: '100%' }}
                whileHover={{ x: '0%' }}
                transition={{ duration: 0.3 }}
              />
            </Button>
            <div className="mt-4 text-sm text-muted-foreground">
              Released on {new Date(update.date).toLocaleDateString()}
            </div>
          </motion.div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default UpdateModal;